import React, { useState } from 'react';
import { User, Lock, LogOut, Save, ChevronRight, Moon, Sun, Monitor } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../api/client';

const ProfileScreen = () => {
  const { user, logout, updateUser } = useAuth();
  const { isDark, toggle: toggleTheme, resetToSystem } = useTheme();

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    new_password2: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSaveProfile = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await authAPI.updateProfile(form);
      updateUser(res.data);
      setEditMode(false);
      setSuccess('Profile updated successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await authAPI.changePassword(passwordForm);
      setSuccess('Password changed. Please log in again.');
      setShowPasswordForm(false);
      setPasswordForm({ old_password: '', new_password: '', new_password2: '' });
      setTimeout(() => logout(), 2000);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        Object.values(err.response?.data || {}).flat().join(' ') ||
        'Failed to change password.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {user?.first_name} {user?.last_name}
            </h1>
            <p className="text-gray-500 text-sm">@{user?.username}</p>
            <p className="text-gray-400 text-xs">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Feedback banners */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-medium">Dismiss</button>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 text-sm flex justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="font-medium">OK</button>
          </div>
        )}

        {/* Profile Details */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-900">Personal Info</h2>
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="text-blue-600 text-sm font-medium hover:underline"
              >
                Edit
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => { setEditMode(false); setForm({ first_name: user?.first_name || '', last_name: user?.last_name || '', email: user?.email || '' }); }}
                  className="text-gray-500 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="flex items-center gap-1 text-blue-600 text-sm font-medium disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" /> Save
                </button>
              </div>
            )}
          </div>

          {editMode ? (
            <div className="space-y-4">
              {[
                { label: 'First Name', key: 'first_name' },
                { label: 'Last Name', key: 'last_name' },
                { label: 'Email', key: 'email', type: 'email' },
              ].map(({ label, key, type = 'text' }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                  <input
                    type={type}
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {[
                { label: 'First Name', value: user?.first_name || '—' },
                { label: 'Last Name', value: user?.last_name || '—' },
                { label: 'Email', value: user?.email || '—' },
                { label: 'Username', value: `@${user?.username}` },
                { label: 'Member Since', value: user?.date_joined ? new Date(user.date_joined).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center py-1">
                  <span className="text-gray-500 text-sm">{label}</span>
                  <span className="text-gray-900 text-sm font-medium">{value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Security */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-gray-900 px-6 pt-5 pb-2">Security</h2>

          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition border-t border-gray-100"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Change Password</span>
            </div>
            <ChevronRight className={`w-4 h-4 text-gray-400 transition ${showPasswordForm ? 'rotate-90' : ''}`} />
          </button>

          {showPasswordForm && (
            <form onSubmit={handleChangePassword} className="px-6 pb-5 space-y-4 border-t border-gray-100 pt-4">
              {[
                { label: 'Current Password', key: 'old_password' },
                { label: 'New Password', key: 'new_password' },
                { label: 'Confirm New Password', key: 'new_password2' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                  <input
                    type="password"
                    value={passwordForm[key]}
                    onChange={(e) => setPasswordForm({ ...passwordForm, [key]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>
              ))}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {loading ? 'Saving...' : 'Update Password'}
              </button>
            </form>
          )}
        </div>

        {/* Appearance */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-gray-900 px-6 pt-5 pb-2">Appearance</h2>

          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              {isDark ? (
                <Moon className="w-4 h-4 text-blue-400" />
              ) : (
                <Sun className="w-4 h-4 text-amber-500" />
              )}
              <div>
                <p className="text-sm text-gray-700">Dark Mode</p>
                <p className="text-xs text-gray-400">{isDark ? 'Enabled' : 'Disabled'}</p>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                isDark ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  isDark ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          <button
            onClick={resetToSystem}
            className="w-full flex items-center gap-3 px-6 py-4 border-t border-gray-100 hover:bg-gray-50 transition"
          >
            <Monitor className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">Use system default</span>
          </button>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-6 py-4 text-sm text-red-600 hover:bg-red-50 transition"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
